import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  GitCompare, 
  FileText, 
  Loader2,
  Check,
  X,
  ArrowLeft,
  Calendar,
  FolderOpen,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Expandable Requirements List Component
const ExpandableRequirementsList = ({ rfpName, requirements }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!requirements || requirements.length === 0) {
    return (
      <div className="p-3 bg-[#F8FAFC] rounded-lg">
        <p className="text-xs font-medium text-[#0F172A] mb-2 truncate">{rfpName}</p>
        <p className="text-xs text-[#94A3B8]">No requirements extracted</p>
      </div>
    );
  }
  
  const displayItems = expanded ? requirements : requirements.slice(0, 3);
  const hasMore = requirements.length > 3;
  
  return (
    <div className="p-3 bg-[#F8FAFC] rounded-lg">
      <p className="text-xs font-medium text-[#0F172A] mb-2 truncate">{rfpName}</p>
      <ul className="space-y-1">
        {displayItems.map((req, i) => (
          <li key={i} className="text-xs text-[#64748B]">• {req}</li>
        ))}
      </ul>
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-[#007AFF] hover:text-[#0066DD] font-medium flex items-center gap-1 transition-colors"
          data-testid={`expand-reqs-${rfpName.substring(0, 10)}`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              +{requirements.length - 3} more
            </>
          )}
        </button>
      )}
    </div>
  );
};

const DocumentComparison = () => {
  const [rfps, setRfps] = useState([]);
  const [selectedRfps, setSelectedRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchRfps();
  }, []);

  const fetchRfps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rfp/list`);
      setRfps(res.data);
    } catch (error) {
      console.error("Error fetching RFPs:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRfp = (rfpId) => {
    setSelectedRfps(prev => {
      if (prev.includes(rfpId)) {
        return prev.filter(id => id !== rfpId);
      }
      if (prev.length >= 3) {
        toast.warning("Maximum 3 RFPs can be compared");
        return prev;
      }
      return [...prev, rfpId];
    });
  };

  const compareDocuments = async () => {
    if (selectedRfps.length < 2) {
      toast.error("Select at least 2 RFPs to compare");
      return;
    }

    setComparing(true);
    try {
      const res = await axios.post(`${API}/rfp/compare`, selectedRfps);
      setResult(res.data);
      setShowResults(true);
    } catch (error) {
      console.error("Error comparing documents:", error);
      toast.error("Failed to compare documents");
    } finally {
      setComparing(false);
    }
  };

  const goBack = () => {
    setShowResults(false);
    setResult(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" />
        </CardContent>
      </Card>
    );
  }

  // Show Results View
  if (showResults && result) {
    return (
      <div className="space-y-6" data-testid="comparison-results">
        {/* Back Button */}
        <Button variant="outline" onClick={goBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Selection
        </Button>

        {/* Header */}
        <div className="flex items-center gap-3">
          <GitCompare className="w-6 h-6 text-[#007AFF]" />
          <div>
            <h2 className="text-xl font-bold text-[#0F172A]">RFP Comparison Results</h2>
            <p className="text-sm text-[#64748B]">Detailed analysis of {result.summary?.total_rfps_compared || 0} RFP documents</p>
          </div>
        </div>

        {/* Summary Stats */}
        {result.summary && (
          <div className="grid grid-cols-5 gap-3">
            <div className="text-center p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              <p className="text-3xl font-bold text-[#007AFF]">{result.summary.total_rfps_compared}</p>
              <p className="text-xs text-[#64748B]">RFPs Compared</p>
            </div>
            <div className="text-center p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              <p className="text-3xl font-bold text-[#0F172A]">{result.summary.avg_pages}</p>
              <p className="text-xs text-[#64748B]">Avg Pages</p>
            </div>
            <div className="text-center p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              <p className="text-3xl font-bold text-[#0F172A]">{result.summary.avg_sections}</p>
              <p className="text-xs text-[#64748B]">Avg Sections</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-3xl font-bold text-emerald-600">{result.summary.total_key_similarities}</p>
              <p className="text-xs text-[#64748B]">Similarities</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-3xl font-bold text-amber-600">{result.summary.total_key_differences}</p>
              <p className="text-xs text-[#64748B]">Differences</p>
            </div>
          </div>
        )}

        {/* Recommendation */}
        {result.recommendation && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 font-medium">{result.recommendation}</p>
          </div>
        )}

        {/* RFP Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.rfps?.map((rfp, idx) => (
            <Card key={rfp.id} className="border-[#E2E8F0]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-[#007AFF]">{idx + 1}</Badge>
                  <span className="font-bold text-sm truncate">{rfp.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-[#F8FAFC] rounded">
                    <p className="text-[#64748B]">Pages</p>
                    <p className="font-bold">{rfp.total_pages || 0}</p>
                  </div>
                  <div className="p-2 bg-[#F8FAFC] rounded">
                    <p className="text-[#64748B]">Sections</p>
                    <p className="font-bold">{rfp.section_count}</p>
                  </div>
                  <div className="p-2 bg-[#F8FAFC] rounded">
                    <p className="text-[#64748B]">Compliance Items</p>
                    <p className="font-bold">{rfp.compliance_count}</p>
                  </div>
                  <div className="p-2 bg-[#F8FAFC] rounded">
                    <p className="text-[#64748B]">High Priority</p>
                    <p className="font-bold text-red-600">{rfp.high_priority_compliance}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-[#64748B] mb-1">Category</p>
                  <Badge variant="outline">{rfp.product_category}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {rfp.departments?.slice(0, 4).map(dept => (
                    <Badge key={dept} variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                      {dept}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Key Similarities */}
        {result.key_similarities?.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-emerald-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Key Similarities ({result.key_similarities.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {result.key_similarities.map((sim, idx) => (
                  <li key={idx} className="text-sm text-emerald-700 flex items-start gap-2 p-2 bg-white/50 rounded">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-xs font-bold uppercase tracking-wide text-emerald-600 mr-2">
                        {sim.category || "General"}
                      </span>
                      <span>{sim.point || sim}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Key Differences */}
        {result.key_differences?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-amber-700 flex items-center gap-2">
                <X className="w-4 h-4" />
                Key Differences ({result.key_differences.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {result.key_differences.map((diff, idx) => (
                  <li key={idx} className="text-sm text-amber-700 flex items-start gap-2 p-2 bg-white/50 rounded">
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wide text-amber-600">
                          {diff.category || "General"}
                        </span>
                        {diff.impact && (
                          <Badge className={`text-xs ${
                            diff.impact === "High" ? "bg-red-100 text-red-700" :
                            diff.impact === "Medium" ? "bg-amber-100 text-amber-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>
                            {diff.impact} Impact
                          </Badge>
                        )}
                      </div>
                      <span>{diff.point || diff}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Scoring Comparison */}
        {result.scoring_comparison?.length > 0 && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Bid Matrix Scores</CardTitle>
              <CardDescription>How each RFP matches your company profile</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-2 px-3 font-medium text-[#64748B]">RFP</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">Overall</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">Eligibility</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">Technical</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {result.scoring_comparison.map((score, idx) => (
                    <tr key={idx} className="border-b border-[#E2E8F0]">
                      <td className="py-2 px-3 truncate max-w-[200px] font-medium">{score.rfp_name}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`font-bold ${
                          score.overall_score >= 70 ? "text-emerald-600" :
                          score.overall_score >= 50 ? "text-amber-600" : "text-red-600"
                        }`}>{score.overall_score}%</span>
                      </td>
                      <td className="py-2 px-3 text-center">{score.eligibility_match}%</td>
                      <td className="py-2 px-3 text-center">{score.technical_match}%</td>
                      <td className="py-2 px-3 text-center">
                        <Badge className={`text-xs ${
                          score.recommendation === "High Priority" ? "bg-emerald-100 text-emerald-700" :
                          score.recommendation === "Medium Priority" ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }`}>{score.recommendation}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Effort Comparison */}
        {result.effort_comparison?.length > 0 && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Effort Estimation</CardTitle>
              <CardDescription>Estimated workload for each RFP</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.effort_comparison.map((effort, idx) => (
                  <div key={idx} className="p-4 bg-[#F8FAFC] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm truncate">{effort.rfp_name}</p>
                      <Badge className={`${
                        effort.effort_level === "High" ? "bg-red-100 text-red-700" :
                        effort.effort_level === "Medium" ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>{effort.effort_level} Effort</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-[#64748B]">Pages</p>
                        <p className="font-bold">{effort.breakdown?.pages}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-[#64748B]">Sections</p>
                        <p className="font-bold">{effort.breakdown?.sections}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-[#64748B]">Compliance</p>
                        <p className="font-bold">{effort.breakdown?.compliance_items}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Comparison */}
        {result.risk_comparison?.length > 0 && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Risk Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {result.risk_comparison.map((risk, idx) => (
                  <div key={idx} className="p-3 bg-[#F8FAFC] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm truncate">{risk.rfp_name}</p>
                      <Badge className={`${
                        risk.risk_level === "High" ? "bg-red-100 text-red-700" :
                        risk.risk_level === "Medium" ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>{risk.risk_level} Risk</Badge>
                    </div>
                    {risk.risks?.length > 0 ? (
                      <ul className="text-xs text-[#64748B] space-y-1">
                        {risk.risks.map((r, i) => (
                          <li key={i} className="flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-emerald-600">No significant risks identified</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compliance Comparison */}
        {result.compliance_comparison?.length > 0 && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Compliance Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-2 px-3 font-medium text-[#64748B]">RFP</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">Total Items</th>
                    <th className="text-center py-2 px-3 font-medium text-[#64748B]">High Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {result.compliance_comparison.map((comp, idx) => (
                    <tr key={idx} className="border-b border-[#E2E8F0]">
                      <td className="py-2 px-3 truncate max-w-[200px]">{comp.rfp_name}</td>
                      <td className="py-2 px-3 text-center font-medium">{comp.total_items}</td>
                      <td className="py-2 px-3 text-center">
                        <Badge className="bg-red-100 text-red-700">{comp.high_priority}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Requirements Comparison */}
        {result.requirements_comparison && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Key Requirements Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Technical Requirements */}
              {Object.keys(result.requirements_comparison.technical || {}).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#64748B] mb-2">Technical Requirements</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(result.requirements_comparison.technical).map(([rfpName, reqs]) => (
                      <ExpandableRequirementsList 
                        key={rfpName} 
                        rfpName={rfpName} 
                        requirements={reqs} 
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Eligibility Requirements */}
              {Object.keys(result.requirements_comparison.eligibility || {}).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#64748B] mb-2">Eligibility Requirements</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(result.requirements_comparison.eligibility).map(([rfpName, reqs]) => (
                      <ExpandableRequirementsList 
                        key={rfpName} 
                        rfpName={rfpName} 
                        requirements={reqs} 
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Deadlines Comparison */}
        {result.deadlines_comparison?.length > 0 && (
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.deadlines_comparison.map((item, idx) => (
                  <div key={idx} className="p-3 bg-[#F8FAFC] rounded-lg">
                    <p className="text-xs font-medium text-[#0F172A] mb-2 truncate">{item.rfp_name}</p>
                    {item.deadlines?.length > 0 ? (
                      <ul className="space-y-1">
                        {item.deadlines.map((d, i) => (
                          <li key={i} className="text-xs text-[#64748B]">
                            • {typeof d === 'object' ? `${d.event || d.description}: ${d.date}` : d}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-[#94A3B8]">No deadlines found</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Selection View
  return (
    <Card className="border-[#E2E8F0]" data-testid="document-comparison">
      <CardHeader>
        <div className="flex items-center gap-2">
          <GitCompare className="w-5 h-5 text-[#007AFF]" />
          <CardTitle className="text-base font-bold">Document Comparison</CardTitle>
        </div>
        <CardDescription>
          Select 2-3 RFPs to compare their sections and requirements
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Selected RFPs */}
        {selectedRfps.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-[#F8FAFC] rounded-lg">
            <span className="text-sm text-[#64748B]">Selected:</span>
            {selectedRfps.map(id => {
              const rfp = rfps.find(r => r.id === id);
              return (
                <Badge key={id} variant="outline" className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {rfp?.name?.substring(0, 30) || id}
                  <button
                    onClick={() => toggleRfp(id)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}

        {/* RFP List */}
        <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
          {rfps.length === 0 ? (
            <div className="text-center py-6 text-[#64748B]">
              <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No RFPs available to compare</p>
            </div>
          ) : (
            rfps.map(rfp => (
              <div
                key={rfp.id}
                className={`p-3 border rounded-lg flex items-center gap-3 cursor-pointer transition-colors ${
                  selectedRfps.includes(rfp.id)
                    ? "border-[#007AFF] bg-blue-50"
                    : "border-[#E2E8F0] hover:border-[#007AFF]/50"
                }`}
                onClick={() => toggleRfp(rfp.id)}
              >
                <Checkbox
                  checked={selectedRfps.includes(rfp.id)}
                  className="pointer-events-none"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[#0F172A] truncate">
                    {rfp.name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#64748B]">
                    <Calendar className="w-3 h-3" />
                    {formatDate(rfp.created_at)}
                    <span>•</span>
                    <span>{rfp.total_pages || "?"} pages</span>
                    {rfp.product_category && (
                      <>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">
                          {rfp.product_category}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Compare Button */}
        <Button
          onClick={compareDocuments}
          disabled={comparing || selectedRfps.length < 2}
          className="w-full bg-[#007AFF] hover:bg-[#0066DD]"
          size="lg"
        >
          {comparing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Comparing...
            </>
          ) : (
            <>
              <GitCompare className="w-4 h-4 mr-2" />
              Compare {selectedRfps.length} Document{selectedRfps.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DocumentComparison;
